/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

function extractMarkdownFromCodeBlock(text: string): string {
  const match = text.match(/```(?:markdown)?\n([\s\S]*?)```/);
  return match ? match[1] : text;
}

type TagActions = {
  ATag?: (sourceId:string|null, startTime:string|null) => void;
  CodeTag?: () => void;
  StrongTag?: () => void;
  OrderedListTag?: () => void;
  UnorderedListTag?: () => void;
  ListItemTag?: () => void;
};

const NonMemoizedMarkdown = ({ 
  children, 
  onActions = {}, 
}: { 
  children: string, 
  onActions?: TagActions; 
}) => {
  const components = {
    code: ({ node, inline, className, children, ...props }: any) => {
      const match = /language-(\w+)/.exec(className || "");
      return !inline && match ? (
        <pre
          {...props}
          className={`w-[80dvw] md:max-w-[500px] overflow-x-auto p-4 rounded-md bg-muted text-muted-foreground text-sm mt-2`}
        >
          <code className={match[1]}>{children}</code>
        </pre>
      ) : (
        <code
          className={`bg-muted text-muted-foreground text-sm px-1 py-0.5 rounded-sm`}
          {...props}
        >
          {children}
        </code>
      );
    },
    ol: ({ node, children, ...props }: any) => {
      return (
        <ol className="list-decimal list-inside ml-4" {...props}>
          {children}
        </ol>
      );
    },
    li: ({ node, children, ...props }: any) => {
      return (
        <li className="py-1" {...props}>
          {children}
        </li>
      );
    },
    ul: ({ node, children, ...props }: any) => {
      return (
        <ul className="list-disc list-inside ml-4" {...props}>
          {children}
        </ul>
      );
    },
    strong: ({ node, children, ...props }: any) => {
      return (
        <span className="font-semibold" {...props}>
          {children}
        </span>
      );
    },
    a: ({ node, children, href, ...props }: any) => {
      if (!href) return <>{children}</>;

      const rawParams = href.split("?")[1];
      const builtParams = new URLSearchParams(rawParams);
      const sourceLink = builtParams.get("sourceLink") || "";
      const isExternalLink = sourceLink.startsWith("https");

      const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (isExternalLink) return;

        e.preventDefault();
        const videoTimeParam = builtParams.get("t");
        onActions?.ATag?.(sourceLink, videoTimeParam);
      };

      const handleExternalHref = () => {
        if (!isExternalLink) return undefined;

        const videoTimeParam = builtParams.get("t");
        let finalUrl = sourceLink;

        if (videoTimeParam && !finalUrl.includes("t=")) {
          finalUrl += `&t=${videoTimeParam}`;
        }

        return finalUrl;
      };

      return (
        <a
          href={isExternalLink ? handleExternalHref() : undefined}
          className="text-blue-500 hover:text-blue-800 font-medium transition-colors hover:cursor-pointer"
          onClick={handleClick}
          target={isExternalLink ? "_blank" : undefined}
          rel={isExternalLink ? "noopener noreferrer" : undefined}
          {...props}
        >
          {children}
        </a>
      );
    }
  };

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {extractMarkdownFromCodeBlock(children)}
    </ReactMarkdown>
  );
};

export const Markdown = React.memo(
  NonMemoizedMarkdown,
  (prevProps, nextProps) => prevProps.children === nextProps.children,
);
